import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export default function useUserRegistrationsLotsModal({
  apiBaseUrl,
  routes,
  apiFetchJson,
  handleNeedsAuth,
  readCache,
  writeCache,
  userAuctionsCacheKey,
  auctions,
  userLotsPageSize,
  normalize,
  isEmail,
  toAbsInt,
  safeNum,
  safeToMillis,
  compareSortValues,
  getDisplayBidderName,
  getDisplayUsername,
  getRegsCount,
  getBidsCount,
  isChargebackEmailSentValue,
  isRegistrationSignedOptIn,
  getChargebackSignDetails,
}) {
  const [userRegsOpen, setUserRegsOpen] = useState(false);
  const [userRegsBusy, setUserRegsBusy] = useState(false);
  const [userRegsErr, setUserRegsErr] = useState("");
  const [userRegsRows, setUserRegsRows] = useState([]);
  const [userRegsUser, setUserRegsUser] = useState(null);
  const [userRegsSortKey, setUserRegsSortKey] = useState("auctionEndAt");
  const [userRegsSortDir, setUserRegsSortDir] = useState("desc");
  const [userRegsModalView, setUserRegsModalView] = useState("registrations");
  const [userLotsRows, setUserLotsRows] = useState([]);
  const [userLotsSortKey, setUserLotsSortKey] = useState("time");
  const [userLotsSortDir, setUserLotsSortDir] = useState("desc");
  const [userLotsBusy, setUserLotsBusy] = useState(false);
  const [userLotsLoadingMore, setUserLotsLoadingMore] = useState(false);
  const [userLotsErr, setUserLotsErr] = useState("");
  const [userLotsHasMore, setUserLotsHasMore] = useState(false);
  const [userLotsCursorOffset, setUserLotsCursorOffset] = useState(0);
  const [userLotsSearchInput, setUserLotsSearchInput] = useState("");
  const [userLotsSearchApplied, setUserLotsSearchApplied] = useState("");
  const [userLotsLoadedOnce, setUserLotsLoadedOnce] = useState(false);
  const [userLotsExportBusy, setUserLotsExportBusy] = useState(false);
  const userLotsContainerRef = useRef(null);

  const userRegsTotals = useMemo(() => {
    const base = Array.isArray(userRegsRows) ? userRegsRows : [];
    let totalLots = 0;
    let totalHammer = 0;
    for (const row of base) {
      totalLots += toAbsInt(row?.lotsCount ?? row?.lotsWon ?? 0);
      totalHammer += safeNum(row?.totalHighBid ?? row?.hammerTotal ?? 0);
    }
    return { totalLots, totalHammer, count: base.length };
  }, [userRegsRows, toAbsInt, safeNum]);

  const userLotsAuctionNameMap = useMemo(() => {
    const output = new Map();
    (Array.isArray(auctions) ? auctions : []).forEach((auction) => {
      const id = String(auction?.auctionId || auction?.id || "").trim();
      if (!id || output.has(id)) return;
      output.set(id, String(auction?.auctionName || id).trim());
    });
    (Array.isArray(userRegsRows) ? userRegsRows : []).forEach((row) => {
      const id = String(row?.auctionId || row?.id || "").trim();
      if (!id || output.has(id)) return;
      output.set(id, String(row?.auctionName || row?.auctionLabel || id).trim());
    });
    return output;
  }, [auctions, userRegsRows]);

  const sortedUserRegsRows = useMemo(() => {
    const dir = userRegsSortDir === "asc" ? 1 : -1;
    const base = Array.isArray(userRegsRows) ? userRegsRows : [];
    const getValue = (row) => {
      switch (userRegsSortKey) {
        case "auctionName":
          return normalize(row?.auctionName);
        case "lotsCount":
          return safeNum(row?.lotsCount ?? row?.lotsWon ?? 0);
        case "totalHighBid":
          return safeNum(row?.totalHighBid ?? row?.hammerTotal ?? 0);
        case "emailSentAt":
          return safeToMillis(row?.emailSentAt) ?? 0;
        case "chargebackEmailSent":
          return isChargebackEmailSentValue(row) ? 1 : 0;
        case "signed":
          return isRegistrationSignedOptIn(row) ? 1 : 0;
        case "auctionEndAt":
        default:
          return safeToMillis(row?.auctionEndAtMs ?? row?.auctionEndMs ?? row?.auctionEndAt) ?? 0;
      }
    };
    return [...base].sort((a, b) => compareSortValues(getValue(a), getValue(b)) * dir);
  }, [userRegsRows, userRegsSortKey, userRegsSortDir, normalize, safeNum, safeToMillis, isChargebackEmailSentValue, isRegistrationSignedOptIn, compareSortValues]);

  const sortedUserLotsRows = useMemo(() => {
    const base = Array.isArray(userLotsRows) ? userLotsRows : [];
    const dir = userLotsSortDir === "asc" ? 1 : -1;
    const getValue = (row) => {
      const auctionId = String(row?.auctionId || "").trim();
      const auctionName = String(row?.auctionName || userLotsAuctionNameMap.get(auctionId) || auctionId || "");
      const lotValue = row?.lot;
      const lotNum = Number(lotValue);
      const lotSortable = Number.isFinite(lotNum) ? lotNum : normalize(lotValue);
      const createdAtMs = safeToMillis(row?.createdAt ?? row?.updatedAt) ?? 0;
      switch (userLotsSortKey) {
        case "auction":
          return normalize(auctionName);
        case "lot":
          return lotSortable;
        case "title":
          return normalize(row?.title);
        case "highBid":
          return safeNum(row?.highBid || 0);
        case "maxBid":
          return safeNum(row?.maxBid || 0);
        case "bids":
          return toAbsInt(row?.bids || 0);
        case "views":
          return toAbsInt(row?.views || 0);
        case "watches":
          return toAbsInt(row?.watches || 0);
        case "consignor":
          return normalize(row?.consignor);
        case "time":
        default:
          return createdAtMs;
      }
    };
    return [...base].sort((a, b) => compareSortValues(getValue(a), getValue(b)) * dir);
  }, [userLotsRows, userLotsSortKey, userLotsSortDir, userLotsAuctionNameMap, normalize, safeToMillis, safeNum, toAbsInt, compareSortValues]);

  const userRegsChargebackSummary = useMemo(() => {
    const rows = Array.isArray(userRegsRows) ? userRegsRows : [];
    let hasSigned = false;
    let hasSent = false;
    for (const row of rows) {
      const details = getChargebackSignDetails(row);
      if (details?.signed) {
        hasSigned = true;
        break;
      }
      if (isChargebackEmailSentValue(row)) hasSent = true;
    }
    if (hasSigned) {
      return {
        label: "Chargeback / Pickup: Signed",
        bg: "rgba(22,163,74,0.16)",
        border: "1px solid rgba(22,163,74,0.35)",
        color: "#166534",
      };
    }
    if (hasSent) {
      return {
        label: "Chargeback / Pickup: Not Signed",
        bg: "rgba(248,113,113,0.16)",
        border: "1px solid rgba(220,38,38,0.35)",
        color: "#991b1b",
      };
    }
    return {
      label: "Chargeback / Pickup: Never Sent",
      bg: "rgba(245,158,11,0.18)",
      border: "1px solid rgba(217,119,6,0.4)",
      color: "#92400e",
    };
  }, [userRegsRows, getChargebackSignDetails, isChargebackEmailSentValue]);

  const fetchUserAuctions = useCallback(async (emailLower, { force = false } = {}) => {
    const email = normalize(emailLower);
    const key = userAuctionsCacheKey(email);
    if (!force) {
      const cached = readCache(key);
      if (Array.isArray(cached?.rows)) return cached.rows;
    }
    const { res, data } = await apiFetchJson(`${apiBaseUrl}${routes.userAuctions}?email=${encodeURIComponent(emailLower)}`);
    if (handleNeedsAuth(res, data)) throw new Error("Authorization required.");
    if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Request failed (${res.status})`);
    const rows = Array.isArray(data?.rows) ? data.rows : [];
    writeCache(key, { rows });
    return rows;
  }, [apiBaseUrl, routes.userAuctions, apiFetchJson, handleNeedsAuth, normalize, readCache, userAuctionsCacheKey, writeCache]);

  const fetchUserLotsPage = useCallback(async (emailLower, { cursorOffset = 0, titleSearch = "" } = {}) => {
    const qs = new URLSearchParams();
    qs.set("email", String(emailLower || "").trim().toLowerCase());
    qs.set("limit", String(userLotsPageSize));
    qs.set("cursorOffset", String(Math.max(0, toAbsInt(cursorOffset))));
    const search = String(titleSearch || "").trim();
    if (search) qs.set("titleSearch", search);
    const { res, data } = await apiFetchJson(`${apiBaseUrl}${routes.userLots}?${qs.toString()}`);
    if (handleNeedsAuth(res, data)) throw new Error("Authorization required.");
    if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Request failed (${res.status})`);
    return data;
  }, [apiBaseUrl, routes.userLots, apiFetchJson, handleNeedsAuth, toAbsInt, userLotsPageSize]);

  const closeUserRegistrationsModal = useCallback(() => {
    setUserRegsOpen(false);
    setUserRegsErr("");
    setUserRegsRows([]);
    setUserRegsUser(null);
    setUserRegsModalView("registrations");
    setUserRegsSortKey("auctionEndAt");
    setUserRegsSortDir("desc");
    setUserLotsRows([]);
    setUserLotsBusy(false);
    setUserLotsLoadingMore(false);
    setUserLotsErr("");
    setUserLotsHasMore(false);
    setUserLotsCursorOffset(0);
    setUserLotsSearchInput("");
    setUserLotsSearchApplied("");
    setUserLotsLoadedOnce(false);
    setUserLotsExportBusy(false);
    setUserLotsSortKey("time");
    setUserLotsSortDir("desc");
  }, []);

  const openUserRegistrationsModal = useCallback(async (userRow) => {
    const emailLower = normalize(userRow?.email || userRow?.id);
    if (!isEmail(emailLower)) return;
    setUserRegsUser(userRow || null);
    setUserRegsOpen(true);
    setUserRegsModalView("registrations");
    setUserRegsBusy(true);
    setUserRegsErr("");
    setUserRegsRows([]);
    setUserRegsSortKey("auctionEndAt");
    setUserRegsSortDir("desc");
    setUserLotsRows([]);
    setUserLotsBusy(false);
    setUserLotsLoadingMore(false);
    setUserLotsErr("");
    setUserLotsHasMore(false);
    setUserLotsCursorOffset(0);
    setUserLotsSearchInput("");
    setUserLotsSearchApplied("");
    setUserLotsLoadedOnce(false);
    try {
      const rows = await fetchUserAuctions(emailLower, { force: true });
      setUserRegsRows(rows);
    } catch (error) {
      setUserRegsErr(error?.message || "Failed to load registrations");
    } finally {
      setUserRegsBusy(false);
    }
  }, [fetchUserAuctions, isEmail, normalize]);

  const loadUserLotsPage = useCallback(async ({ append = false, titleSearch = userLotsSearchApplied } = {}) => {
    const emailLower = normalize(userRegsUser?.email || userRegsUser?.id);
    if (!isEmail(emailLower)) return;
    if (append && (!userLotsHasMore || userLotsLoadingMore || userLotsBusy)) return;
    if (!append && userLotsBusy) return;

    if (append) setUserLotsLoadingMore(true);
    else setUserLotsBusy(true);
    setUserLotsErr("");
    try {
      const cursorOffset = append ? userLotsCursorOffset : 0;
      const data = await fetchUserLotsPage(emailLower, { cursorOffset, titleSearch });
      const rows = Array.isArray(data?.lots) ? data.lots : [];
      setUserLotsRows((prev) => (append ? [...prev, ...rows] : rows));
      setUserLotsHasMore(!!data?.hasMore);
      setUserLotsCursorOffset(toAbsInt(data?.nextCursorOffset ?? 0));
      if (!append && userLotsContainerRef.current) userLotsContainerRef.current.scrollTop = 0;
      if (!append) setUserLotsLoadedOnce(true);
    } catch (error) {
      setUserLotsErr(error?.message || "Failed to load lots");
      if (!append) {
        setUserLotsRows([]);
        setUserLotsHasMore(false);
        setUserLotsCursorOffset(0);
        setUserLotsLoadedOnce(true);
      }
    } finally {
      if (append) setUserLotsLoadingMore(false);
      else setUserLotsBusy(false);
    }
  }, [
    fetchUserLotsPage,
    isEmail,
    normalize,
    toAbsInt,
    userLotsBusy,
    userLotsCursorOffset,
    userLotsHasMore,
    userLotsLoadingMore,
    userLotsSearchApplied,
    userRegsUser,
  ]);

  useEffect(() => {
    if (!userRegsOpen || userRegsModalView !== "lots") return;
    if (userLotsBusy || userLotsLoadingMore) return;
    if (userLotsLoadedOnce) return;
    void loadUserLotsPage({ append: false, titleSearch: userLotsSearchApplied });
  }, [loadUserLotsPage, userRegsOpen, userRegsModalView, userLotsBusy, userLotsLoadedOnce, userLotsLoadingMore, userLotsSearchApplied]);

  const runUserLotsSearch = useCallback(async () => {
    const query = String(userLotsSearchInput || "").trim();
    setUserLotsSearchApplied(query);
    setUserLotsCursorOffset(0);
    setUserLotsHasMore(false);
    setUserLotsLoadedOnce(false);
    await loadUserLotsPage({ append: false, titleSearch: query });
  }, [loadUserLotsPage, userLotsSearchInput]);

  const loadMoreUserLots = useCallback(async () => {
    if (userRegsModalView !== "lots") return;
    if (userLotsBusy || userLotsLoadingMore || !userLotsHasMore) return;
    await loadUserLotsPage({ append: true, titleSearch: userLotsSearchApplied });
  }, [loadUserLotsPage, userLotsBusy, userLotsHasMore, userLotsLoadingMore, userLotsSearchApplied, userRegsModalView]);

  const onUserLotsScroll = useCallback(async (evt) => {
    const element = evt?.currentTarget;
    if (!element) return;
    const nearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 140;
    if (!nearBottom) return;
    await loadMoreUserLots();
  }, [loadMoreUserLots]);

  return {
    userRegsOpen,
    userRegsBusy,
    userRegsErr,
    userRegsRows,
    setUserRegsRows,
    userRegsUser,
    userRegsSortKey,
    setUserRegsSortKey,
    userRegsSortDir,
    setUserRegsSortDir,
    userRegsModalView,
    setUserRegsModalView,
    userLotsRows,
    userLotsSortKey,
    setUserLotsSortKey,
    userLotsSortDir,
    setUserLotsSortDir,
    userLotsBusy,
    userLotsLoadingMore,
    userLotsErr,
    setUserLotsErr,
    userLotsHasMore,
    userLotsSearchInput,
    setUserLotsSearchInput,
    userLotsExportBusy,
    setUserLotsExportBusy,
    userLotsContainerRef,
    userRegsTotals,
    userLotsAuctionNameMap,
    sortedUserRegsRows,
    sortedUserLotsRows,
    userRegsChargebackSummary,
    openUserRegistrationsModal,
    closeUserRegistrationsModal,
    fetchUserAuctions,
    fetchUserLotsPage,
    runUserLotsSearch,
    onUserLotsScroll,
    getDisplayBidderName,
    getDisplayUsername,
    getRegsCount,
    getBidsCount,
  };
}
